export const WEIGHT_LOG_HP_REWARD = 1;
export const WEIGHT_GOAL_HP_PER_KG = 30;
export const MIN_WEIGHT_KG = 30;
export const MAX_WEIGHT_KG = 400;
export const MIN_WEIGHT_GOAL_KG = 40;
export const MAX_WEIGHT_GOAL_KG = 200;
export const WEIGHT_GOAL_KG_STEP = 0.5;
export const MIN_WEIGHT_GOAL_TOLERANCE_KG = 0.5;
export const MAX_WEIGHT_GOAL_TOLERANCE_KG = 10;
export const WEIGHT_GOAL_TOLERANCE_KG_STEP = 0.5;
export const DEFAULT_WEIGHT_GOAL_TOLERANCE_KG = 2;

export function isWeightGoalCompletableIntent(intent: string | null | undefined): boolean {
    return intent === 'LOSE' || intent === 'GAIN';
}

export function snapWeightGoalKg(kg: number): number {
    return +(Math.round(kg / WEIGHT_GOAL_KG_STEP) * WEIGHT_GOAL_KG_STEP).toFixed(1);
}

export const WEIGHT_GOAL_KG_VALUES: number[] = (() => {
    const out: number[] = [];
    for (let v = MIN_WEIGHT_GOAL_KG; v <= MAX_WEIGHT_GOAL_KG + 1e-9; v += WEIGHT_GOAL_KG_STEP) {
        out.push(+v.toFixed(1));
    }
    return out;
})();

export type WeightKgValidation = { ok: true; kg: number } | { ok: false; message: string };

export type WeightGoalValidation = { ok: true; kg: number | null } | { ok: false; message: string };

function validateWeightInRange(
    n: number,
    min: number,
    max: number,
    invalidMessage: string,
    rangeMessage: string,
    normalize: (value: number) => number,
): WeightKgValidation {
    if (!Number.isFinite(n)) {
        return { ok: false, message: invalidMessage };
    }
    if (n < min || n > max) {
        return { ok: false, message: rangeMessage };
    }
    return { ok: true, kg: normalize(n) };
}

export function validateWeightKg(n: number): WeightKgValidation {
    return validateWeightInRange(
        n,
        MIN_WEIGHT_KG,
        MAX_WEIGHT_KG,
        'Enter a valid weight',
        `Weight must be between ${MIN_WEIGHT_KG} and ${MAX_WEIGHT_KG} kg`,
        value => +value.toFixed(1),
    );
}

export function validateWeightGoalKg(n: number | null | undefined): WeightGoalValidation {
    if (n == null || !Number.isFinite(n) || n <= 0) {
        return { ok: true, kg: null };
    }
    return validateWeightInRange(
        n,
        MIN_WEIGHT_GOAL_KG,
        MAX_WEIGHT_GOAL_KG,
        'Enter a valid weight goal',
        `Weight goal must be between ${MIN_WEIGHT_GOAL_KG} and ${MAX_WEIGHT_GOAL_KG} kg`,
        snapWeightGoalKg,
    );
}

export function snapWeightGoalToleranceKg(kg: number): number {
    return +(Math.round(kg / WEIGHT_GOAL_TOLERANCE_KG_STEP) * WEIGHT_GOAL_TOLERANCE_KG_STEP).toFixed(1);
}

export function validateWeightGoalToleranceKg(n: number): WeightKgValidation {
    return validateWeightInRange(
        n,
        MIN_WEIGHT_GOAL_TOLERANCE_KG,
        MAX_WEIGHT_GOAL_TOLERANCE_KG,
        'Enter a valid range',
        `Range must be between ${MIN_WEIGHT_GOAL_TOLERANCE_KG} and ${MAX_WEIGHT_GOAL_TOLERANCE_KG} kg`,
        snapWeightGoalToleranceKg,
    );
}

export type WeightCorridorRange = { lo: number; hi: number };

export function weightCorridorRange(anchorKg: number, toleranceKg: number): WeightCorridorRange {
    return { lo: +(anchorKg - toleranceKg).toFixed(1), hi: +(anchorKg + toleranceKg).toFixed(1) };
}

export type WeightCorridorStatus = 'IN_RANGE' | 'ABOVE_RANGE' | 'BELOW_RANGE';

export function getWeightCorridorStatus(currentKg: number, anchorKg: number, toleranceKg: number): WeightCorridorStatus {
    const { lo, hi } = weightCorridorRange(anchorKg, toleranceKg);
    if (currentKg > hi) return 'ABOVE_RANGE';
    if (currentKg < lo) return 'BELOW_RANGE';
    return 'IN_RANGE';
}

export function parseWeightGoalRaw(raw: string): WeightGoalValidation {
    const t = raw.trim();
    if (t === '' || t === '0' || t === '0.0') {
        return { ok: true, kg: null };
    }
    const n = Number.parseFloat(t.replace(',', '.'));
    return validateWeightGoalKg(n);
}

export function normalizeWeightKg(n: number): number {
    const checked = validateWeightKg(n);
    if (!checked.ok) throw new Error('INVALID_WEIGHT_KG');
    return checked.kg;
}

export function normalizeWeightGoalKg(n: number | null | undefined): number | null {
    const checked = validateWeightGoalKg(n);
    if (!checked.ok) throw new Error('INVALID_WEIGHT_KG');
    return checked.kg;
}

export function isWeightGoalMet(currentKg: number, goalKg: number, initialKg: number): boolean {
    if (Math.abs(initialKg - goalKg) < 0.05) return false;
    if (initialKg > goalKg) return currentKg <= goalKg;
    return currentKg >= goalKg;
}

export type WeightDateEntry = { dateKey: string; kg: number };

export function findWeightGoalAchievedDateKey(entries: WeightDateEntry[], goalKg: number, initialKg: number): string | null {
    const sorted = [...entries].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    for (const entry of sorted) {
        if (isWeightGoalMet(entry.kg, goalKg, initialKg)) return entry.dateKey;
    }
    return null;
}

export function isWeightGoalRecapPending(
    currentKg: number | null,
    goalKg: number | null,
    initialKg: number | null,
    weightGoalAchievedAt: string | null | undefined,
    weightIntent?: string | null,
): boolean {
    if (weightGoalAchievedAt != null && weightGoalAchievedAt !== '') return false;
    if (weightIntent != null && !isWeightGoalCompletableIntent(weightIntent)) return false;
    if (goalKg == null || initialKg == null || currentKg == null) return false;
    return isWeightGoalMet(currentKg, goalKg, initialKg);
}

export type WeightGoalProgress = {
    pct: number;
    remaining: number;
    donePct: number;
    achieved: boolean;
};

export function weightGoalProgress(currentKg: number, goalKg: number, initialKg: number | null, goalAchieved: boolean): WeightGoalProgress {
    if (goalAchieved) {
        return { pct: 100, remaining: 0, donePct: 100, achieved: true };
    }
    const origin = initialKg ?? currentKg;
    const span = Math.abs(origin - goalKg);
    if (span <= 0) {
        return { pct: 0, remaining: Math.abs(currentKg - goalKg), donePct: 0, achieved: false };
    }
    const done = Math.abs(origin - currentKg);
    const pct = Math.min(100, Math.max(0, (done / span) * 100));
    const achieved = isWeightGoalMet(currentKg, goalKg, origin);
    return {
        pct: achieved ? 100 : pct,
        remaining: Math.abs(currentKg - goalKg),
        donePct: achieved ? 100 : Math.round(pct),
        achieved,
    };
}

export function weightGoalKgDelta(initialKg: number, goalKg: number): number {
    return +Math.abs(initialKg - goalKg).toFixed(1);
}

export type WeightGoalRecapHp = {
    baseHp: number;
    streakBonusHp: number;
    totalHp: number;
    kgDelta: number;
};

export function computeWeightGoalRecapHp(
    initialKg: number,
    goalKg: number,
    streakDays: number,
    streakDividendHp: (streak: number, gained: number, lost: number) => number,
): WeightGoalRecapHp {
    const kgDelta = weightGoalKgDelta(initialKg, goalKg);
    const baseHp = Math.round(WEIGHT_GOAL_HP_PER_KG * kgDelta);
    const streakBonusHp = streakDividendHp(streakDays, baseHp, 0);
    return { baseHp, streakBonusHp, totalHp: baseHp + streakBonusHp, kgDelta };
}

export function calendarDaysInclusive(fromDateKey: string, toDateKey: string): number {
    const from = new Date(`${fromDateKey}T12:00:00`);
    const to = new Date(`${toDateKey}T12:00:00`);
    const diff = Math.round((to.getTime() - from.getTime()) / 86_400_000);
    return Math.max(1, diff + 1);
}

export type WeightMaintenanceStreak = {
    streakStartDateKey: string | null;
    streakDays: number;
    streakWeeks: number;
};

export function computeWeightMaintenanceStreak(
    status: WeightCorridorStatus,
    previousStreakStartDateKey: string | null,
    dateKey: string,
): WeightMaintenanceStreak {
    if (status !== 'IN_RANGE') {
        return { streakStartDateKey: null, streakDays: 0, streakWeeks: 0 };
    }
    const streakStartDateKey = previousStreakStartDateKey ?? dateKey;
    const streakDays = calendarDaysInclusive(streakStartDateKey, dateKey);
    return { streakStartDateKey, streakDays, streakWeeks: Math.floor(streakDays / 7) };
}
